import React, { useEffect, useMemo, useState } from "react";
import "./OrderReportsPage.css";
import { getReportPeriod } from "../reportPeriod";
import { formatDateInKyiv, formatKyivTime, isTodayInKyiv, kyivDateTimeAtCurrentTime, shiftDateByDays } from "../kyivTime";
import {
    getOrderExpenseSettings,
    getOrderReportByDayEnterprise,
    getOrderReportByEnterprise,
    getOrderReportBySupplier,
    getOrderReportSummary,
    getOrderReportUnresolvedCost,
    recalculateOrderExpenses,
    setOrderReportItemManualCost,
    syncOrderReports,
    upsertOrderExpenseSetting,
} from "../api/orderReportsApi";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "18px",
    maxWidth: "1440px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const kpiToneClass = {
    default: "",
    blue: "tone-blue",
    good: "tone-good",
    bad: "tone-bad",
    warn: "tone-warn",
};

const tabs = [
    ["overview", "Обзор"],
    ["enterprise", "По предприятиям"],
    ["supplier", "По поставщикам"],
    ["problems", "Проблемные заказы"],
    ["settings", "Настройки расходов"],
];

const today = new Date();
const { periodFrom: defaultPeriodFrom, periodTo: defaultPeriodTo } = getReportPeriod("current_month", new Date(`${formatDateInKyiv(today)}T12:00:00`));

const formatAmount = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value || "0");
};

const percentOf = (numerator, denominator) => {
    const numeratorValue = Number(numerator || 0);
    const denominatorValue = Number(denominator || 0);
    if (!Number.isFinite(numeratorValue) || !Number.isFinite(denominatorValue) || denominatorValue === 0) {
        return "0,00%";
    }
    return `${formatAmount((numeratorValue * 100) / denominatorValue)}%`;
};

const MetricCard = ({ label, value, valueStyle, secondLabel, secondValue, tone = "default" }) => (
    <div className={`or-kpi${kpiToneClass[tone] ? ` ${kpiToneClass[tone]}` : ""}`}>
        <span className="lbl">{label}</span>
        <span className="val" style={valueStyle}>{value}</span>
        {secondLabel ? (
            <div className="sub">
                <span className="slbl">{secondLabel}</span>
                <span className="sval">{secondValue}</span>
            </div>
        ) : null}
    </div>
);

const chartColors = ["#2563eb", "#16a34a", "#f59e0b", "#8b5cf6", "#db2777", "#0891b2", "#65a30d", "#ea580c"];

const formatDayLabel = (value) => {
    const [year, month, day] = String(value || "").split("-");
    return year && month && day ? `${day}.${month}` : value;
};

const getLastSevenDaysPeriod = (periodTo) => ({
    periodFrom: shiftDateByDays(periodTo, -6),
    periodTo,
});

const OrderDailyChart = ({ rows, comparisonRows, comparisonCutoff }) => {
    const [selected, setSelected] = useState(null);
    const enterprises = useMemo(() => {
        const byCode = new Map();
        rows.forEach((day) => (day.enterprises || []).forEach((enterprise) => {
            if (!byCode.has(enterprise.enterprise_code)) {
                byCode.set(enterprise.enterprise_code, { ...enterprise, orders_count: 0 });
            }
            byCode.get(enterprise.enterprise_code).orders_count += Number(enterprise.orders_count || 0);
        }));
        return [...byCode.values()]
            .sort((left, right) => left.enterprise_name.localeCompare(right.enterprise_name, "ru"))
            .map((enterprise, index) => ({ ...enterprise, color: chartColors[index % chartColors.length] }));
    }, [rows]);
    const enterpriseByCode = useMemo(() => new Map(enterprises.map((enterprise) => [enterprise.enterprise_code, enterprise])), [enterprises]);
    const totalOrders = rows.reduce((total, day) => total + Number(day.total_orders || 0), 0);
    const comparisonTotal = comparisonRows.reduce((total, day) => total + Number(day.total_orders || 0), 0);
    const maxOrders = Math.max(1, ...rows.map((day) => Number(day.total_orders || 0)), ...comparisonRows.map((day) => Number(day.total_orders || 0)));
    const chartTrackHeight = 220;
    const chartLabelHeight = 30;
    const chartTotalHeight = chartLabelHeight + chartTrackHeight + 20;
    const pointForComparison = (value, index) => ({
        x: ((index + 0.5) / Math.max(rows.length, 1)) * 100,
        y: ((maxOrders - Number(value || 0)) / maxOrders) * 100,
    });
    const comparisonPoints = rows.map((_, index) => pointForComparison(comparisonRows[index]?.total_orders, index));
    const comparisonPolyline = comparisonPoints.map((point) => `${point.x},${point.y}`).join(" ");

    if (!rows.length) {
        return null;
    }

    return (
        <section className="or-card or-daily-chart">
            <div className="or-panel-head">
                <div><h3>Заказы по дням</h3><p>Активные и проданные заказы по Business-предприятиям за последние 7 дней</p></div>
                <p>Всего за период: <b>{totalOrders}</b> заказов</p>
            </div>
            <div className="or-daily-chart-scroll">
                <div className="or-daily-chart-bars" style={{ "--or-chart-columns": rows.length }} role="list" aria-label="Заказы по дням">
                    <svg className="or-chart-comparison-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        <polyline points={comparisonPolyline} />
                    </svg>
                    {rows.map((day) => {
                        const total = Number(day.total_orders || 0);
                        return (
                            <div className="or-chart-column" key={day.date} role="listitem">
                                <span className="or-chart-total">{total}</span>
                                <div className="or-chart-track">
                                    <div className="or-chart-stack" style={{ height: `${(total / maxOrders) * 100}%` }}>
                                        {(day.enterprises || []).map((segment) => {
                                            const enterprise = enterpriseByCode.get(segment.enterprise_code);
                                            const count = Number(segment.orders_count || 0);
                                            const isSelected = selected?.type === "enterprise" && selected.date === day.date && selected.enterpriseCode === segment.enterprise_code;
                                            return (
                                                <button
                                                    key={segment.enterprise_code}
                                                    type="button"
                                                    className={`or-chart-segment${isSelected ? " selected" : ""}`}
                                                    style={{ height: `${(count / total) * 100}%`, backgroundColor: enterprise?.color }}
                                                    aria-label={`${enterprise?.enterprise_name}: ${count} заказов`}
                                                    onClick={() => {
                                                        setSelected(isSelected ? null : {
                                                            type: "enterprise",
                                                            date: day.date,
                                                            enterpriseCode: segment.enterprise_code,
                                                            enterpriseName: enterprise?.enterprise_name,
                                                            count,
                                                            total,
                                                        });
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                <span className="or-chart-day">{formatDayLabel(day.date)}</span>
                            </div>
                        );
                    })}
                    {rows.map((day, index) => {
                        const point = comparisonPoints[index];
                        const count = Number(comparisonRows[index]?.total_orders || 0);
                        const isSelected = selected?.type === "comparison" && selected.date === day.date;
                        const cutoffLabel = index === rows.length - 1 && comparisonCutoff ? ` до ${comparisonCutoff}` : "";
                        return <button key={`comparison-${day.date}`} type="button" className={`or-chart-comparison-point${isSelected ? " selected" : ""}`} style={{ left: `${point.x}%`, top: `${((chartLabelHeight + (point.y / 100) * chartTrackHeight) / chartTotalHeight) * 100}%` }} aria-label={`${formatDayLabel(day.date)}, Заказы 4 недели назад${cutoffLabel}: ${count}`} onClick={() => setSelected(isSelected ? null : { type: "comparison", date: day.date, count, cutoffLabel })} />;
                    })}
                </div>
            </div>
            {selected ? <div className="or-chart-tooltip" role="status"><b>{formatDayLabel(selected.date)}</b><span>{selected.type === "comparison" ? `Заказы 4 недели назад${selected.cutoffLabel || ""}` : selected.enterpriseName}</span><strong>{selected.count} заказов{selected.type === "enterprise" ? ` · ${percentOf(selected.count, selected.total)}` : ""}</strong></div> : null}
            <div className="or-chart-legend" aria-label="Легенда магазинов">
                {enterprises.map((enterprise) => (
                    <div className="or-chart-legend-item" key={enterprise.enterprise_code}>
                        <span className="or-chart-legend-color" style={{ backgroundColor: enterprise.color }} />
                        <span>{enterprise.enterprise_name}</span>
                        <b>{enterprise.orders_count} · {percentOf(enterprise.orders_count, totalOrders)}</b>
                    </div>
                ))}
                <div className="or-chart-legend-item or-chart-comparison-legend"><span className="or-chart-line-swatch" /><span>Заказы 4 недели назад</span><b>{comparisonTotal}</b></div>
            </div>
        </section>
    );
};

const OrderReportsPage = () => {
    const [periodFrom, setPeriodFrom] = useState(defaultPeriodFrom);
    const [periodTo, setPeriodTo] = useState(defaultPeriodTo);
    const [periodPreset, setPeriodPreset] = useState("current_month");
    const [enterpriseCode, setEnterpriseCode] = useState("");
    const [summary, setSummary] = useState(null);
    const [byEnterprise, setByEnterprise] = useState([]);
    const [byDayEnterprise, setByDayEnterprise] = useState([]);
    const [comparisonByDayEnterprise, setComparisonByDayEnterprise] = useState([]);
    const [comparisonCutoff, setComparisonCutoff] = useState("");
    const [bySupplier, setBySupplier] = useState([]);
    const [settings, setSettings] = useState([]);
    const [unresolvedItems, setUnresolvedItems] = useState([]);
    const [activeTab, setActiveTab] = useState("overview");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [settingsDrafts, setSettingsDrafts] = useState({});
    const [costDrafts, setCostDrafts] = useState({});
    const [savingItemId, setSavingItemId] = useState(null);

    const enterpriseOptions = summary?.business_enterprises || [];

    const applyPeriodPreset = (preset) => {
        setPeriodPreset(preset);
        if (preset === "custom") return;
        const range = getReportPeriod(preset);
        setPeriodFrom(range.periodFrom);
        setPeriodTo(range.periodTo);
    };

    const loadReports = async () => {
        setLoading(true);
        setError("");
        try {
            const params = { periodFrom, periodTo, enterpriseCode: enterpriseCode || null };
            const dailyChartPeriod = getLastSevenDaysPeriod(periodTo);
            const now = new Date();
            const endsToday = isTodayInKyiv(periodTo, now);
            const comparisonEndDate = shiftDateByDays(dailyChartPeriod.periodTo, -28);
            const comparisonEndAt = endsToday ? kyivDateTimeAtCurrentTime(comparisonEndDate, now) : comparisonEndDate;
            setComparisonCutoff(endsToday ? formatKyivTime(comparisonEndAt) : "");
            const dailyChartParams = {
                ...dailyChartPeriod,
                enterpriseCode: enterpriseCode || null,
            };
            const comparisonParams = {
                periodFrom: shiftDateByDays(dailyChartPeriod.periodFrom, -28),
                periodTo: comparisonEndAt,
                enterpriseCode: enterpriseCode || null,
            };
            const [summaryData, enterpriseData, dailyEnterpriseData, comparisonDailyEnterpriseData, supplierData, settingsData, unresolvedData] = await Promise.all([
                getOrderReportSummary(params),
                getOrderReportByEnterprise(params),
                getOrderReportByDayEnterprise(dailyChartParams),
                getOrderReportByDayEnterprise(comparisonParams),
                getOrderReportBySupplier(params),
                getOrderExpenseSettings(),
                getOrderReportUnresolvedCost(params),
            ]);
            setSummary(summaryData);
            setByEnterprise(enterpriseData || []);
            setByDayEnterprise(dailyEnterpriseData || []);
            setComparisonByDayEnterprise(comparisonDailyEnterpriseData || []);
            setBySupplier(supplierData || []);
            setSettings(settingsData || []);
            setUnresolvedItems(unresolvedData?.rows || []);
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить отчеты по заказам.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runSync = async () => {
        setLoading(true);
        setMessage("");
        setError("");
        try {
            const result = await syncOrderReports({ periodFrom, periodTo, enterpriseCode: enterpriseCode || null });
            const reasons = result.failed_reasons ? `, reasons=${JSON.stringify(result.failed_reasons)}` : "";
            const errorMessage = result.error_message ? `, error=${result.error_message}` : "";
            const skipped = result.skipped_count ? `, skipped=${result.skipped_count}` : "";
            setMessage(`Sync: ${result.status}, created=${result.created_count}, updated=${result.updated_count}, failed=${result.failed_count}${skipped}${reasons}${errorMessage}`);
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Sync не выполнен.");
            setLoading(false);
        }
    };

    const saveSetting = async (row) => {
        setLoading(true);
        setError("");
        setMessage("");
        const draft = settingsDrafts[row.enterprise_code] || {};
        try {
            await upsertOrderExpenseSetting({
                enterprise_code: row.enterprise_code,
                expense_percent: draft.expense_percent ?? row.expense_percent ?? "0",
                active_from: draft.active_from ?? row.active_from ?? defaultPeriodFrom,
                active_to: draft.active_to ?? row.active_to ?? null,
            });
            const result = await recalculateOrderExpenses({ periodFrom, periodTo, enterpriseCode: row.enterprise_code });
            setMessage(`Расходы сохранены. Пересчитано строк: ${result.updated_count || 0}`);
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Настройка расходов не сохранена.");
            setLoading(false);
        }
    };

    const saveItemCost = async (item) => {
        const draftValue = costDrafts[item.item_id];
        const costPrice = Number(String(draftValue ?? "").replace(",", "."));
        if (!Number.isFinite(costPrice) || costPrice < 0) {
            setError("Себестоимость должна быть неотрицательным числом.");
            return;
        }
        setSavingItemId(item.item_id);
        setError("");
        setMessage("");
        try {
            await setOrderReportItemManualCost({ itemId: item.item_id, costPrice });
            setMessage(`Себестоимость заказа ${item.order_number || item.item_id} сохранена: ${costPrice}.`);
            setCostDrafts((prev) => {
                const next = { ...prev };
                delete next[item.item_id];
                return next;
            });
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось сохранить себестоимость.");
        } finally {
            setSavingItemId(null);
        }
    };

    const unresolvedCostItemsCount = Number(summary?.unresolved_cost_items_count || 0);
    const unresolvedCostOrdersCount = Number(summary?.unresolved_cost_orders_count || 0);
    const unresolvedCostAmount = Number(summary?.unresolved_cost_amount || 0);
    const hasUnresolvedCost = unresolvedCostItemsCount > 0 || unresolvedCostOrdersCount > 0 || unresolvedCostAmount > 0;

    return (
        <div className="order-reports-page" style={pageStyle}>
            <div className="or-headrow">
                <div className="or-htitle">
                    <h1>Отчеты по заказам</h1>
                    <span className="or-crumb">Business enterprises, статусы, продажи, отказы, возвраты и прибыль.</span>
                </div>
                <div className="or-hacts">
                    <select className="or-inp sm" style={{ width: 178 }} value={periodPreset} onChange={(e) => applyPeriodPreset(e.target.value)}>
                        <option value="current_month">Текущий месяц (по сегодня)</option>
                        <option value="previous_month">Предыдущий месяц</option>
                        <option value="custom">Произвольный период</option>
                    </select>
                    <input className="or-inp sm" style={{ width: 132 }} type="date" value={periodFrom} onChange={(e) => { setPeriodFrom(e.target.value); setPeriodPreset("custom"); }} />
                    <input className="or-inp sm" style={{ width: 132 }} type="date" value={periodTo} onChange={(e) => { setPeriodTo(e.target.value); setPeriodPreset("custom"); }} />
                    <select className="or-inp sm" style={{ width: 190 }} value={enterpriseCode} onChange={(e) => setEnterpriseCode(e.target.value)}>
                        <option value="">Все Business</option>
                        {enterpriseOptions.map((item) => (
                            <option key={item.enterprise_code} value={item.enterprise_code}>
                                {item.enterprise_name} ({item.enterprise_code})
                            </option>
                        ))}
                    </select>
                    <button type="button" className="or-btn or-btn-primary or-btn-sm" onClick={loadReports} disabled={loading}>Обновить</button>
                    <button type="button" className="or-btn or-btn-ghost or-btn-sm" onClick={runSync} disabled={loading}>Sync SalesDrive</button>
                </div>
            </div>

            {(loading || error || message) && (
                <div className={`or-note${error ? " err" : ""}`}>
                    <span className="dot" />
                    {loading ? "Загрузка..." : error || message}
                </div>
            )}

            {hasUnresolvedCost && (
                <div className="or-callout warn">
                    <span className="ic">⚠</span>
                    <div>Неподтвержденная себестоимость: {unresolvedCostOrdersCount} заказов / {unresolvedCostItemsCount} позиций / {formatAmount(unresolvedCostAmount)}. Прибыль предварительная.</div>
                </div>
            )}

            <div className="or-kpi-grid">
                <MetricCard label="Сумма заказов" value={formatAmount(summary?.order_financial_amount)} secondLabel="Выручка продаж" secondValue={formatAmount(summary?.sale_amount)} tone="blue" />
                <MetricCard
                    label="ВП заказов / %ВП"
                    value={<>{formatAmount(summary?.order_gross_profit_amount)} / <span style={{ color: "var(--or-text)" }}>{percentOf(summary?.order_gross_profit_amount, summary?.order_financial_amount)}</span></>}
                    valueStyle={{ fontSize: 17, whiteSpace: "nowrap" }}
                    secondLabel="ЧП заказов"
                    secondValue={formatAmount(summary?.order_net_profit_amount)}
                    tone="good"
                />
                <MetricCard label="ВП продаж" value={formatAmount(summary?.gross_profit_amount)} secondLabel="ЧП продаж" secondValue={formatAmount(summary?.net_profit_amount)} tone="good" />
                <MetricCard label="Количество заказов" value={summary?.order_financial_count || 0} secondLabel="Количество продаж" secondValue={summary?.sales_count || 0} tone="default" />
                <MetricCard label="Отказы" value={summary?.cancelled_count || 0} secondLabel="% отказов" secondValue={`${formatAmount(summary?.refusal_rate)}%`} tone="bad" />
                <MetricCard label="Возвраты" value={summary?.return_count || 0} secondLabel="% возвратов" secondValue={`${formatAmount(summary?.return_rate)}%`} tone="warn" />
            </div>

            <OrderDailyChart rows={byDayEnterprise} comparisonRows={comparisonByDayEnterprise} comparisonCutoff={comparisonCutoff} />

            <div className="or-subtabs">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={`or-subtab${activeTab === key ? " active" : ""}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {activeTab === "overview" && (
                <div className="or-view">
                    <div className="or-split2">
                        <section className="or-card">
                            <div className="or-panel-head">
                                <h3>Предварительно</h3>
                                <p>По заказам за период, включая ещё не завершённые</p>
                            </div>
                            <div className="or-panel-pad or-tbl-wrap">
                                <table className="or-tbl kv"><tbody>
                                    <tr><td>Количество заказов в расчете</td><td>{summary?.order_financial_count || 0}</td></tr>
                                    <tr><td>Сумма заказов в расчете</td><td>{formatAmount(summary?.order_financial_amount)}</td></tr>
                                    <tr><td>Штук в заказах</td><td>{formatAmount(summary?.order_financial_quantity)}</td></tr>
                                    <tr><td>Себестоимость заказов</td><td>{formatAmount(summary?.order_supplier_cost_total)}</td></tr>
                                    <tr>
                                        <td>Валовая прибыль заказов / %ВП</td>
                                        <td>{formatAmount(summary?.order_gross_profit_amount)} / {percentOf(summary?.order_gross_profit_amount, summary?.order_financial_amount)}</td>
                                    </tr>
                                    <tr><td>Расходы заказов</td><td>{formatAmount(summary?.order_expense_amount)}</td></tr>
                                    <tr className="total"><td>Чистая прибыль заказов</td><td>{formatAmount(summary?.order_net_profit_amount)}</td></tr>
                                    {hasUnresolvedCost && <tr className="hl"><td>Неподтвержденная себестоимость</td><td>{unresolvedCostOrdersCount} / {formatAmount(unresolvedCostAmount)}</td></tr>}
                                    <tr><td>Активные / в работе</td><td>{summary?.active_orders || 0}</td></tr>
                                    <tr><td>Отказы</td><td>{summary?.cancelled_count || 0} / {formatAmount(summary?.refusal_rate)}%</td></tr>
                                    <tr><td>Возвраты</td><td>{summary?.return_count || 0} / {formatAmount(summary?.return_rate)}%</td></tr>
                                    <tr><td>Удаленные</td><td>{summary?.deleted_count || 0}</td></tr>
                                </tbody></table>
                            </div>
                        </section>
                        <section className="or-card">
                            <div className="or-panel-head">
                                <h3>Окончательно по продажам</h3>
                                <p>Только завершённые продажи за период</p>
                            </div>
                            <div className="or-panel-pad or-tbl-wrap">
                                <table className="or-tbl kv"><tbody>
                                    <tr><td>Количество продаж</td><td>{summary?.sales_count || 0}</td></tr>
                                    <tr><td>Сумма продаж</td><td>{formatAmount(summary?.sale_amount)}</td></tr>
                                    <tr><td>Штук продано</td><td>{formatAmount(summary?.sale_quantity)}</td></tr>
                                    <tr><td>Себестоимость</td><td>{formatAmount(summary?.supplier_cost_total)}</td></tr>
                                    <tr>
                                        <td>Валовая прибыль / %ВП</td>
                                        <td>{formatAmount(summary?.gross_profit_amount)} / {percentOf(summary?.gross_profit_amount, summary?.sale_amount)}</td>
                                    </tr>
                                    <tr><td>Расходы</td><td>{formatAmount(summary?.expense_amount)}</td></tr>
                                    <tr className="total"><td>Чистая прибыль</td><td>{formatAmount(summary?.net_profit_amount)}</td></tr>
                                </tbody></table>
                            </div>
                        </section>
                    </div>
                </div>
            )}

            {activeTab === "enterprise" && <div className="or-view"><ReportTable rows={byEnterprise} /></div>}
            {activeTab === "supplier" && <div className="or-view"><SupplierTable rows={bySupplier} /></div>}
            {activeTab === "problems" && (
                <div className="or-view">
                    <UnresolvedCostTable
                        rows={unresolvedItems}
                        costDrafts={costDrafts}
                        setCostDrafts={setCostDrafts}
                        savingItemId={savingItemId}
                        onSave={saveItemCost}
                    />
                </div>
            )}
            {activeTab === "settings" && (
                <div className="or-view">
                    <section className="or-card">
                        <div className="or-panel-head"><h3>Настройки расходов</h3><p>Процент операционных расходов по предприятию, применяется к заказам в периоде действия</p></div>
                        <div className="or-panel-pad or-tbl-wrap">
                            <table className="or-tbl">
                                <thead><tr><th>Предприятие</th><th className="num">Expense %</th><th>Active from</th><th>Active to</th><th>Действия</th></tr></thead>
                                <tbody>
                                    {settings.map((row) => {
                                        const draft = settingsDrafts[row.enterprise_code] || {};
                                        return (
                                            <tr key={`${row.enterprise_code}-${row.setting_id || "new"}`}>
                                                <td><span className="or-nm">{row.enterprise_name}</span><span className="or-sub-meta">{row.enterprise_code}</span></td>
                                                <td className="num">
                                                    <input
                                                        className="or-inp sm"
                                                        style={{ width: 90, textAlign: "right" }}
                                                        value={draft.expense_percent ?? row.expense_percent ?? "0"}
                                                        onChange={(e) => setSettingsDrafts((prev) => ({ ...prev, [row.enterprise_code]: { ...(prev[row.enterprise_code] || {}), expense_percent: e.target.value } }))}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="or-inp sm"
                                                        type="date"
                                                        value={draft.active_from ?? row.active_from ?? defaultPeriodFrom}
                                                        onChange={(e) => setSettingsDrafts((prev) => ({ ...prev, [row.enterprise_code]: { ...(prev[row.enterprise_code] || {}), active_from: e.target.value } }))}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="or-inp sm"
                                                        type="date"
                                                        value={draft.active_to ?? row.active_to ?? ""}
                                                        onChange={(e) => setSettingsDrafts((prev) => ({ ...prev, [row.enterprise_code]: { ...(prev[row.enterprise_code] || {}), active_to: e.target.value } }))}
                                                    />
                                                </td>
                                                <td><button type="button" className="or-btn or-btn-ghost or-btn-sm" onClick={() => saveSetting(row)}>Сохранить</button></td>
                                            </tr>
                                        );
                                    })}
                                    {!settings.length ? <tr className="or-empty-row"><td colSpan={5}>Нет настроенных предприятий</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </section>
                    <div className="or-callout">
                        <span className="ic">ⓘ</span>
                        <div>Изменение процента и пересохранение запускает пересчёт расходов и чистой прибыли по заказам предприятия за выбранный в тулбаре период.</div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ReportTable = ({ rows }) => (
    <section className="or-card">
        <div className="or-panel-head"><h3>По предприятиям</h3><p>Business enterprises за выбранный период</p></div>
        <div className="or-panel-pad or-tbl-wrap">
            <table className="or-tbl">
                <thead>
                    <tr>
                        <th>Предприятие</th><th className="num">Заказы</th><th className="num">Продажи</th><th className="num">Отказы</th>
                        <th className="num">% отказов</th><th className="num">Возвраты</th><th className="num">% возвратов</th>
                        <th className="num">Выручка</th><th className="num">Валовая прибыль</th><th className="num">% ВП</th><th className="num">Чистая прибыль</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.enterprise_code}>
                            <td><span className="or-nm">{row.enterprise_name}</span><span className="or-sub-meta">{row.enterprise_code}</span></td>
                            <td className="num">{row.total_orders}</td>
                            <td className="num">{row.sales_count}</td>
                            <td className="num">{row.cancelled_count}</td>
                            <td className="num">{formatAmount(row.refusal_rate)}%</td>
                            <td className="num">{row.return_count}</td>
                            <td className="num">{formatAmount(row.return_rate)}%</td>
                            <td className="num">{formatAmount(row.sale_amount)}</td>
                            <td className="num">{formatAmount(row.gross_profit_amount)}</td>
                            <td className="num">{percentOf(row.gross_profit_amount, row.sale_amount)}</td>
                            <td className="num">{formatAmount(row.net_profit_amount)}</td>
                        </tr>
                    ))}
                    {!rows.length ? <tr className="or-empty-row"><td colSpan={11}>Нет данных за период</td></tr> : null}
                </tbody>
            </table>
        </div>
    </section>
);

const SupplierTable = ({ rows }) => (
    <section className="or-card">
        <div className="or-panel-head"><h3>По поставщикам</h3><p>Верхняя строка — продажи, нижняя (приглушённая) — заказы</p></div>
        <div className="or-panel-pad or-tbl-wrap">
            <table className="or-tbl">
                <thead>
                    <tr>
                        <th>Поставщик</th><th>Тип</th><th className="num">Заказы</th><th className="num">Штук</th><th className="num">Сумма</th>
                        <th className="num">Себестоимость</th><th className="num">Валовая</th><th className="num">% ВП</th><th className="num">Доля</th>
                        <th className="num">Расходы</th><th className="num">Чистая</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <React.Fragment key={`${row.supplier_code}-${row.supplier_name}`}>
                            <tr>
                                <td rowSpan={2}><span className="or-nm">{row.supplier_name}</span><span className="or-sub-meta">{row.supplier_code}</span></td>
                                <td>Продажи</td>
                                <td className="num">{row.sales_count}</td>
                                <td className="num">{formatAmount(row.quantity)}</td>
                                <td className="num">{formatAmount(row.sale_amount)}</td>
                                <td className="num">{formatAmount(row.cost_amount)}</td>
                                <td className="num">{formatAmount(row.gross_profit_amount)}</td>
                                <td className="num">{percentOf(row.gross_profit_amount, row.sale_amount)}</td>
                                <td className="num">{formatAmount(row.sales_share_percent)}%</td>
                                <td className="num">{formatAmount(row.allocated_expense_amount)}</td>
                                <td className="num">{formatAmount(row.net_profit_amount)}</td>
                            </tr>
                            <tr className="sub">
                                <td>Заказы</td>
                                <td className="num">{row.orders_count}</td>
                                <td className="num">{formatAmount(row.order_quantity)}</td>
                                <td className="num">{formatAmount(row.order_amount)}</td>
                                <td className="num">{formatAmount(row.order_cost_amount)}</td>
                                <td className="num">{formatAmount(row.order_gross_profit_amount)}</td>
                                <td className="num">{percentOf(row.order_gross_profit_amount, row.order_amount)}</td>
                                <td className="num">{formatAmount(row.order_share_percent)}%</td>
                                <td className="num">{formatAmount(row.order_allocated_expense_amount)}</td>
                                <td className="num">{formatAmount(row.order_net_profit_amount)}</td>
                            </tr>
                        </React.Fragment>
                    ))}
                    {!rows.length ? <tr className="or-empty-row"><td colSpan={11}>Нет данных за период</td></tr> : null}
                </tbody>
            </table>
        </div>
    </section>
);

const UnresolvedCostTable = ({ rows, costDrafts, setCostDrafts, savingItemId, onSave }) => (
    <section className="or-card">
        <div className="or-panel-head">
            <h3>Проблемные заказы</h3>
            <p>Позиции без подтверждённой себестоимости за выбранный период — прибыль по ним предварительная</p>
        </div>
        <div className="or-panel-pad or-tbl-wrap">
            <table className="or-tbl">
                <thead>
                    <tr>
                        <th>Заказ</th><th>Дата</th><th>Предприятие</th><th>SKU / товар</th><th>Поставщик</th>
                        <th className="num">Сумма продажи</th><th>Причина</th><th className="num">Себестоимость</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((item) => (
                        <tr key={item.item_id}>
                            <td><span className="or-nm">{item.order_number || item.external_order_id}</span><span className="or-sub-meta">{item.source}</span></td>
                            <td>{item.order_created_at ? new Date(item.order_created_at).toLocaleDateString("ru-RU") : "—"}</td>
                            <td>{item.enterprise_code}</td>
                            <td><span className="or-nm">{item.sku}</span><span className="or-sub-meta">{item.product_name}</span></td>
                            <td><span className="or-nm">{item.supplier_name}</span><span className="or-sub-meta">{item.supplier_code}</span></td>
                            <td className="num">{formatAmount(item.sale_amount)}</td>
                            <td><span className="or-sub-meta">{item.cost_unresolved_reason}</span></td>
                            <td className="num">
                                <input
                                    className="or-inp sm"
                                    style={{ width: 100, textAlign: "right" }}
                                    placeholder="0.00"
                                    value={costDrafts[item.item_id] ?? ""}
                                    onChange={(e) => setCostDrafts((prev) => ({ ...prev, [item.item_id]: e.target.value }))}
                                />
                            </td>
                            <td>
                                <button
                                    type="button"
                                    className="or-btn or-btn-ghost or-btn-sm"
                                    disabled={savingItemId === item.item_id}
                                    onClick={() => onSave(item)}
                                >
                                    Сохранить
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!rows.length ? <tr className="or-empty-row"><td colSpan={9}>Нет заказов с неподтверждённой себестоимостью за период</td></tr> : null}
                </tbody>
            </table>
        </div>
    </section>
);

export default OrderReportsPage;
